import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import type { AdoptionCase, AdoptionCaseStatus } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../../ui/components';

const STAFF_ROLES = ['SOCIAL_WORKER', 'SENIOR_SW'];
const SENIOR_ROLES = ['SENIOR_SW'];

/** Linear happy-path state machine — mirrors ADVANCE_RULES/PREDECESSOR in m27-family-affairs/src/routes/adoptions.ts exactly. */
const NEXT_STATUS: Partial<Record<AdoptionCaseStatus, AdoptionCaseStatus>> = {
  APPLIED: 'ASSESSMENT',
  ASSESSMENT: 'PANEL_REVIEW',
  PANEL_REVIEW: 'APPROVED',
  APPROVED: 'MATCHED',
  MATCHED: 'PLACED',
  PLACED: 'FINALISED',
};
const REQUIRED_FIELDS: Partial<Record<AdoptionCaseStatus, string[]>> = {
  APPROVED: ['panelDecision'],
  MATCHED: ['childNin', 'childName'],
  FINALISED: ['m01AdoptionRecordId'],
};
const DECLINABLE_FROM: AdoptionCaseStatus[] = ['APPLIED', 'ASSESSMENT', 'PANEL_REVIEW'];

/**
 * Adoption case tracking — GET/POST /api/v1/m27/adoption-cases/*. Like
 * foster-carers, the intake endpoint (POST /adoption-cases) is citizen
 * self-service gated on the filing applicant's own verified NIN — no
 * staff-side "file on behalf of" endpoint exists, so this screen tracks
 * cases through assessment/panel-review/approval/matching/placement/
 * finalisation rather than creating new ones.
 */
export function Adoptions() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isStaff = roles.some((r) => STAFF_ROLES.includes(r));
  const isSenior = roles.some((r) => SENIOR_ROLES.includes(r));

  const [statusFilter, setStatusFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    return `/api/v1/m27/adoption-cases?${p}`;
  }, [statusFilter]);
  const listQ = useApiQuery<{ items: AdoptionCase[]; nextCursor: string | null }>(isStaff ? query : null);

  if (!isStaff) {
    return (
      <div>
        <PageHeader title="Adoptions" />
        <ErrorNote message="This view requires the Social Worker or Senior Social Worker role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Adoption Case Tracking" subtitle="Assessment → panel review → approval → matching → placement → finalisation" />
      <div className="mb-4 flex flex-wrap gap-3">
        <select className="field-input max-w-[200px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="APPLIED">Applied</option>
          <option value="ASSESSMENT">Assessment</option>
          <option value="PANEL_REVIEW">Panel review</option>
          <option value="APPROVED">Approved</option>
          <option value="MATCHED">Matched</option>
          <option value="PLACED">Placed</option>
          <option value="FINALISED">Finalised</option>
          <option value="DECLINED">Declined</option>
          <option value="WITHDRAWN">Withdrawn</option>
        </select>
      </div>

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.error ? (
          <ErrorNote message={listQ.error} />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No adoption cases found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Applicants</th>
                <th className="table-head">District</th>
                <th className="table-head">Child</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{a.refNo}</td>
                  <td className="table-cell text-xs">{a.applicants.map((p) => p.name).join(' & ')}</td>
                  <td className="table-cell text-xs">{a.district.replaceAll('-', ' ')}</td>
                  <td className="table-cell text-xs">{a.childName ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenId(a.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId ? (
        <AdoptionDetailModal id={openId} isSenior={isSenior} onClose={() => setOpenId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function AdoptionDetailModal({
  id,
  isSenior,
  onClose,
  onChanged,
}: {
  id: string;
  isSenior: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<AdoptionCase>(`/api/v1/m27/adoption-cases/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [panelDecision, setPanelDecision] = useState('');
  const [childNin, setChildNin] = useState('');
  const [childName, setChildName] = useState('');
  const [m01RecordId, setM01RecordId] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const refresh = () => {
    detailQ.reload();
    onChanged();
  };

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  const data = detailQ.data;
  const next = data ? NEXT_STATUS[data.status] : undefined;
  const required = next ? (REQUIRED_FIELDS[next] ?? []) : [];
  const canAdvance =
    !next ||
    ((!required.includes('panelDecision') || panelDecision.length >= 3) &&
      (!required.includes('childNin') || childNin.length >= 5) &&
      (!required.includes('childName') || childName.length >= 2) &&
      (!required.includes('m01AdoptionRecordId') || m01RecordId.length >= 1));

  const advance = () =>
    run(async () => {
      if (!next) return;
      await api.post(`/api/v1/m27/adoption-cases/${id}/advance`, {
        toStatus: next,
        panelDecision: required.includes('panelDecision') ? panelDecision : undefined,
        childNin: required.includes('childNin') ? childNin : undefined,
        childName: required.includes('childName') ? childName : undefined,
        m01AdoptionRecordId: required.includes('m01AdoptionRecordId') ? m01RecordId : undefined,
      });
    });

  return (
    <Modal title={data ? `Adoption case — ${data.refNo}` : 'Adoption case'} onClose={onClose} wide>
      {detailQ.loading ? (
        <Spinner />
      ) : detailQ.error ? (
        <ErrorNote message={detailQ.error} />
      ) : data ? (
        <div className="space-y-5">
          {error ? <ErrorNote message={error} /> : null}

          <div>
            <StatusBadge status={data.status} />
            <div className="mt-1 text-lg font-bold text-slate-900">{data.applicants.map((p) => p.name).join(' & ')}</div>
            <div className="text-xs text-slate-500">
              District {data.district.replaceAll('-', ' ')} · {data.applicants.map((p) => p.nin).join(', ')}
            </div>
            {data.childName ? (
              <div className="mt-1 text-sm text-slate-700">
                Child: <strong>{data.childName}</strong> ({data.childNin})
              </div>
            ) : null}
            {data.panelDecision ? (
              <p className="mt-1 text-xs text-slate-500">
                Panel decision ({formatDateTime(data.panelDecisionAt)}): {data.panelDecision}
              </p>
            ) : null}
            {data.declineReason ? <ErrorNote message={`Declined: ${data.declineReason}`} /> : null}
            {data.m01AdoptionRecordId ? <p className="mt-1 text-xs text-slate-400">M01 sealed record: {data.m01AdoptionRecordId}</p> : null}
          </div>

          {next ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="field-label mb-2">Advance to {next.replaceAll('_', ' ')}</div>
              <div className="space-y-2">
                {required.includes('panelDecision') ? (
                  <Field label="Panel decision">
                    <textarea className="field-input" rows={2} value={panelDecision} onChange={(e) => setPanelDecision(e.target.value)} />
                  </Field>
                ) : null}
                {required.includes('childNin') || required.includes('childName') ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Child NIN">
                      <input className="field-input" value={childNin} onChange={(e) => setChildNin(e.target.value)} />
                    </Field>
                    <Field label="Child name">
                      <input className="field-input" value={childName} onChange={(e) => setChildName(e.target.value)} />
                    </Field>
                  </div>
                ) : null}
                {required.includes('m01AdoptionRecordId') ? (
                  <Field label="M01 sealed adoption record ID (confirmed out-of-band by M01's Sealed Records Officer)">
                    <input className="field-input" value={m01RecordId} onChange={(e) => setM01RecordId(e.target.value)} />
                  </Field>
                ) : null}
              </div>
              <div className="mt-2 flex justify-end">
                <button className="btn-primary !py-1 text-xs" disabled={busy || !canAdvance} onClick={advance}>
                  {busy ? 'Advancing…' : `Advance to ${next.replaceAll('_', ' ')}`}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500">No further advance is available from status {data.status}.</p>
          )}

          {isSenior && DECLINABLE_FROM.includes(data.status) ? (
            <div>
              {showDecline ? (
                <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Decline reason" className="flex-1">
                    <input className="field-input" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger !py-2"
                    disabled={busy || declineReason.length < 5}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/adoption-cases/${id}/decline`, { reason: declineReason });
                      setDeclineReason(''); setShowDecline(false);
                    })}
                  >
                    Confirm decline
                  </button>
                </div>
              ) : (
                <button className="btn-danger !py-1 text-xs" onClick={() => setShowDecline(true)}>
                  Decline case
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
